import DropdownSelector from '@/components/dropdown-selector/DropdownSelector';
import React, { FC, useEffect, useState } from 'react';
import { Button } from '../../../button/Button';
import { WizardStepLayout } from '../../../wizard/wizard-summary-layout';
import { COMMAND_STATE, MESSAGE_OVERLOAD_ERROR, MONITORING_COMMAND, MONITORING_COMMAND_STATE } from '../../../../constants/command-status';
import Commands from '../../../../constants/commands';
import { Direction } from '../../../../constants/Direction';
import { DEF_PG_SIZE_WIZARD } from '../../../../constants/maximum-page-size';
import { useLocale } from '../../../../contexts/localeContext';
import { TableContextProvider } from '../../../../contexts/table/TableContextProvider';
import { TranslationKeys } from '../../../../contexts/TranslationKeys';
import { useIcaWebsocketContext } from '../../../../contexts/websocket/IcaWebsocketContext';
import { BiometricWizardType } from '../../../../models/Biometric';
import columns from '../../../../models/ColumnDefinitions/DiscoverBiometricColumns';
import { ExecuteCommand } from '../../../../models/ExecuteCommand';
import { ListData } from '../../../../models/ListData';
import { MultiSelectOption } from '../../../../models/MultiSelectOption';
import { PaginationModel } from '../../../../models/Pagination';
import searchColumns from '../../../../models/search/DiscoverBiometricSearch';
import { SearchTerm } from '../../../../models/search/SearchTerm';
import { SortBy } from '../../../../models/SortBy';
import { executeCommand } from '../../../../services/executeCommandService';
import { getSystemUserIpRanges } from '../../../../services/userPreferenceService';
import { getErrorMessage } from '../../../../services/utilities/ServiceError';
import ActivityFrame from '../../../activity-frame/activity-frame';
import { useDialog } from '../../../dialog/DialogContext';
import TableTab from '../../../tabs/table-tab';
import Tooltip from '../../../Tooltip/Tooltip';
import { AddIpRangeModal } from '../ipAddressWizard/AddIpRangeModal';
import IPRanges from '../ipAddressWizard/IPRanges';
import { SingleIPSearchModal } from '../ipAddressWizard/SingleIPSearchModal';

interface SelectDeviceProps {
    setSelectedBiometricDevices: any;
    onNext: (e) => void;
    onBack: (e) => void;
    selectedBrand: any;
}

const initialPagination = {
    totalCount: 0,
    maxPageSize: DEF_PG_SIZE_WIZARD,
    totalPages: 0,
    pageNumber: 1
};

const SelectDevice: FC<SelectDeviceProps> = ({ onNext, onBack, setSelectedBiometricDevices, selectedBrand }) => {
    const { translate } = useLocale();
    const title = translate.byKey(TranslationKeys.selectADeviceToInstall);
    const [busy, setBusy] = useState(false);
    const [openAddRangeModal, setOpenAddRangeModal] = useState<boolean>(false);
    const [openIPRangeSelection, setOpenIPRangeSelection] = useState<boolean>(false);
    const [openIPSearch, setOpenIPSearch] = useState<boolean>(false);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [advanceScanOptions, setAdvanceScanOptions] = useState<MultiSelectOption[]>([]);
    const commands = {
        SELECT_RANGE: '0',
        ADD_RANGE: '1',
        SPECIFIC_IP: '2'
    }
    const dialog = useDialog();
    const [pagination] = useState(initialPagination);
    const [rows] = useState(new Array(0));
    const [paginatedResults] = useState({ rows, pagination });
    const icaWebSocket = useIcaWebsocketContext();
    const [allRows] = useState(new Array(0));

    useEffect(() => {
        getSystemUserIpRanges().then((ranges) => {
            if (ranges.rows.length > 0) {
                setAdvanceScanOptions([
                    { value: commands.SELECT_RANGE, label: translate.byKey(TranslationKeys.selectRange) },
                    { value: commands.ADD_RANGE, label: translate.byKey(TranslationKeys.addRange) },
                    { value: commands.SPECIFIC_IP, label: translate.byKey(TranslationKeys.specificIP) }
                ]);

                return;
            }

            setAdvanceScanOptions([
                { value: commands.ADD_RANGE, label: translate.byKey(TranslationKeys.addRange) },
                { value: commands.SPECIFIC_IP, label: translate.byKey(TranslationKeys.specificIP) }
            ]);
        }).catch(() => {

        })
    }, [openAddRangeModal, openIPRangeSelection])

    useEffect(() => {
        if (icaWebSocket.connectionState) {
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_DISCOVERY, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_START);
            icaWebSocket.setOnMessage(onMessage)
        }

        return () => {
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_DEVICE, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);
        }
    }, [icaWebSocket.connectionState])

    const onMessage = (data) => {
        if (!data.data.includes(MESSAGE_OVERLOAD_ERROR)) {
            const response = JSON.parse(data.data);
            let isComplete: boolean = false;
            if (response.command === MONITORING_COMMAND.MONITORING_DISCOVERY && response.attributes.discovery?.length > 0) {
                response.attributes.discovery.forEach((discovery) => {
                    if (discovery.cmdState === COMMAND_STATE.FOUND || discovery.cmdState === COMMAND_STATE.COMMAND_COMPLETE) {
                        const newDiscoveries: BiometricWizardType[] = discovery.biometrics.map((biometric): BiometricWizardType => {
                            return {
                                address: biometric.attributes.address,
                                ipAddress: biometric.attributes.ip,
                                serialAddress: biometric.attributes.serial,
                                version: biometric.attributes.version,
                                baseDeviceName: biometric.attributes.baseDeviceName,
                                name: '',
                                id: biometric.attributes.address,
                                baAddress: '',
                                biometricTypeId: biometric.attributes.bioTypeId,
                                baseDeviceType: biometric.attributes.baseDeviceType,
                            }
                        }).filter((newDiscovery: BiometricWizardType) => {
                            return !allRows.map((discovery) => discovery.serialAddress)
                                .includes(newDiscovery.serialAddress)
                        });

                        if (rows.length < pagination.maxPageSize) {
                            rows.push(...newDiscoveries);
                        }

                        allRows.push(...newDiscoveries);
                        pagination.totalCount = allRows.length;
                        pagination.totalPages = Math.ceil(allRows.length / pagination.maxPageSize);
                    }

                    if (discovery.cmdState === COMMAND_STATE.COMMAND_COMPLETE) {
                        isComplete = true;
                    }
                })

                // Hide the busy state if the scan has completed
                if (isComplete) {
                    setBusy(false)
                }
            }
        }
    }

    const sortRows = (data: any[], sortBy?: SortBy) => {
        const columnName = sortBy?.columnName;
        const newDirection = sortBy?.direction;

        if (columnName) {
            data.sort((rowA, rowB) => {
                const direction = newDirection === Direction.ascending
                    ? rowA[columnName] > rowB[columnName]
                    : rowB[columnName] > rowA[columnName]
                return direction ? 1 : -1;
            });
        }
    }

    const searchRows = (data: any[], searchTerm?: SearchTerm) => {
        const columnName = searchTerm?.accessor;
        const searchValue = searchTerm?.value;

        if (columnName && searchValue) {
            const validResults = allRows.filter((row) => {
                return row[columnName].includes(searchValue);
            });
            data.splice(0);
            validResults.forEach((row) => {
                data.push(row);
            });
        }
    }

    const paginateRows = (data: any[], pagination: PaginationModel, goToPage, pageSize) => {
        data.splice(pagination.pageNumber * pagination.maxPageSize);
        data.splice(0, (pagination.pageNumber - 1) * pagination.maxPageSize)
    }

    const resetRows = (data: any[], originalData: any[]) => {
        data.splice(0, data.length, ...originalData);
    }

    const scanNetwork = (values) => {
        const startIp = values.startIp ? values.startIp : undefined;
        const endIp = values.endIp ? values.endIp : undefined;
        const command: ExecuteCommand = {
            cmd: Commands.Discovery.label,
            cmdSubType: selectedBrand.value,
            startIp: startIp,
            endIp: endIp
        }

        setBusy(true);
        executeCommand(command)
            .then(() => {
            })
            .catch((error) => {
                setBusy(false);
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) })
            })
    }

    const handleBack = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        allRows.splice(0);
        rows.splice(0);
        pagination.pageNumber = initialPagination.pageNumber;
        pagination.totalCount = initialPagination.totalCount;
        pagination.totalPages = initialPagination.totalPages;
        pagination.maxPageSize = initialPagination.maxPageSize;
        onBack(e);
    };

    const handleNext = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        if (selectedIds.length > 0) {
            generateBiometricDevicesToInstall();
            onNext(e);
        }
    };

    const generateBiometricDevicesToInstall = async () => {
        let selectedBiometricDevices = [] as any;
        const biometricDevices = await fetchBiometricData(listParameters => listParameters);
        biometricDevices.rows.filter((device) => {
            if (selectedIds.includes(device.id.toString())) {
                selectedBiometricDevices.push({
                    address: device.address,
                    ip: device.ipAddress,
                    serial: device.serialAddress,
                    version: device.version,
                    baseDeviceName: device.baseDeviceName,
                    bioTypeId: device.biometricTypeId,
                    baseDeviceType: device.baseDeviceType
                });
            }
        })
        setSelectedBiometricDevices(selectedBiometricDevices);
    }

    const fetchBiometricData = (listParameters): Promise<ListData> => {
        resetRows(paginatedResults.rows, allRows);
        sortRows(paginatedResults.rows, listParameters.sortBy);
        searchRows(paginatedResults.rows, listParameters.searchTerm);
        paginatedResults.pagination.pageNumber = listParameters.goToPage ?? paginatedResults.pagination.pageNumber;
        paginatedResults.pagination.maxPageSize = listParameters.pageSize ?? paginatedResults.pagination.maxPageSize;
        paginateRows(paginatedResults.rows, paginatedResults.pagination, listParameters.goToPage, listParameters.pageSize);
 
        return new Promise((resolve) => {
            return resolve(paginatedResults);
        });
    };

    const checkAdvancedScanOptions = (advancedScanOption: string) => {
        if (advancedScanOption === commands.ADD_RANGE) {
            setOpenAddRangeModal(true);
        }
        else if (advancedScanOption === commands.SELECT_RANGE) {
            setOpenIPRangeSelection(true);
        }
        else if (advancedScanOption === commands.SPECIFIC_IP) {
            setOpenIPSearch(true);
        }
    }


    return (
        <WizardStepLayout
            onBack={handleBack}
            onNext={handleNext}
            nextDisabled={selectedIds.length === 0}
            tertiaryActions={
                <div className="flex flex-row gap-2">
                    <Button variant="tertiary" onClick={scanNetwork}>
                        <Tooltip text={translate.byKey(TranslationKeys.scanSubnet)} direction='top' show={true}>
                            <span>{translate.byKey(TranslationKeys.scanSubnet)}</span>
                        </Tooltip>
                    </Button>
                    <DropdownSelector
                        buttonClassName="secondary secondary-tertiary py-1 px-5 flex items-center gap-1"
                        onSelected={(event) => checkAdvancedScanOptions(event.value)}
                        options={advanceScanOptions}
                        direction='top'>
                        {translate.byKey(TranslationKeys.advancedScan)}
                    </DropdownSelector>
                </div>
            }>
            <ActivityFrame busy={busy} busyMessage={translate.byKey(TranslationKeys.scanning)} className={`flex ${busy ? 'h-full w-full justify-center' : ''} flex-wrap content-center`}>
                <div className='flex w-full' style={{ maxHeight: '410px' }}>
                    <TableContextProvider
                        dataMethod={fetchBiometricData}
                        useSearch
                        useFilter
                        usePagination
                        useSort
                        useTable
                        tableDescriptor={columns}
                        searchDescriptor={searchColumns}
                        canExportReport={false}
                        emptyStateMessage={translate.byKey(TranslationKeys.zeroDevicesFound)}>
                        <TableTab
                            title={title}
                            selectRows={true}
                            selectedIds={selectedIds}
                            setSelectedIds={setSelectedIds}
                            cyName='selectDevice'/>
                    </TableContextProvider>
                </div>
            </ActivityFrame>
            <AddIpRangeModal isOpen={openAddRangeModal} onclose={() => setOpenAddRangeModal(false)} />
            <IPRanges isOpen={openIPRangeSelection} onClose={() => { setOpenIPRangeSelection(false) }} handleOnSubmit={(values) => scanNetwork(values)} />
            <SingleIPSearchModal isOpen={openIPSearch} onClose={() => { setOpenIPSearch(false) }} handleOnSubmit={(values) => scanNetwork(values)} />
        </WizardStepLayout>
    );
}

export default SelectDevice;
